import {
  ActionIcon, Avatar, Badge, Button, Card, Center, Divider, FileButton, Group,
  Image, Loader, Modal, NumberInput, PasswordInput, Select, SimpleGrid, Stack,
  Switch, Table, Tabs, Text, Textarea, TextInput, Tooltip,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconCalendarPlus, IconCheck, IconEdit, IconPhoto, IconPlus, IconReceipt,
  IconTrash, IconX,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useResource, useRemove, useInfiniteResource, apiClient } from "../../../shared/api";
import {
  PageHeader, MoneyDisplay, EmptyState, InfiniteTable, confirmAction, confirmDelete,
  validateDate, validatePhoneTR, validateEmail,
} from "../../../shared/ui";

const ROLE_OPTIONS = [
  { value: "WAITER", label: "Garson" },
  { value: "CHEF", label: "Şef" },
  { value: "KITCHEN_STAFF", label: "Mutfak Personeli" },
  { value: "CASHIER", label: "Kasiyer" },
  { value: "MANAGER", label: "Yönetici" },
  { value: "ACCOUNTANT", label: "Muhasebeci" },
];

/**
 * DatePickerInput değerini (Date | string | null) YYYY-MM-DD string'e çevir.
 * Boş/null ise "" döner. Yerel saat dilimine göre — UTC kayması olmaz.
 */
function toDateStr(v: Date | string | null): string {
  if (!v) return "";
  if (typeof v === "string") return v.slice(0, 10);
  const y = v.getFullYear();
  const m = String(v.getMonth() + 1).padStart(2, "0");
  const d = String(v.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** YYYY-MM-DD string'i DatePickerInput'un beklediği Date | null'a çevir. */
function toDateValue(s: string): Date | null {
  if (!s) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Telefonu kayıt için standart 05XXXXXXXXX biçimine getir (yalnızca
 * rakamlar; +90/90 ön ekini 0'a çevirir). Doğrulama validatePhoneTR ile
 * ayrı yapılır; bu yalnızca depolama biçimini sabitler.
 */
function normalizePhone(v: string): string {
  let digits = (v ?? "").replace(/\D/g, "");
  if (digits.startsWith("90") && digits.length === 12) digits = "0" + digits.slice(2);
  else if (digits.length === 10) digits = "0" + digits;
  return digits;
}

export function PersonnelPage() {
  const qc = useQueryClient();
  const { data: profileData, isLoading } = useResource<any>("waiter-profiles");
  const { data: eotmData } = useResource<any>("employee-of-month");
  const remove = useRemove("waiter-profiles");

  // Tek modal hem "Yeni Personel" hem "Personel Düzenle" için kullanılır
  // (aynı bileşen → düzenlemede de tüm alanlar görünür).
  const [formOpened, formCtl] = useDisclosure(false);
  const [editing, setEditing] = useState<any | null>(null);

  const profiles: any[] = profileData?.results || profileData || [];
  const eotm: any[] = eotmData?.results || eotmData || [];

  if (isLoading) return <Center h={300}><Loader /></Center>;

  function startCreate() {
    setEditing(null);
    formCtl.open();
  }

  function startEdit(p: any) {
    setEditing(p);
    formCtl.open();
  }

  async function handleDelete(p: any) {
    if (!(await confirmDelete(`${p.display_name || p.username} silinsin mi?`))) return;
    try {
      await remove.mutateAsync(p.id);
      notifications.show({ message: "Silindi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  // Kullanıcıyı kapat/aç — Membership.is_active toggle (login'i etkiler).
  // onboard idempotent; mevcut profili is_active ile günceller.
  async function handleToggleActive(p: any) {
    const next = !p.is_active;
    const verb = next ? "aktifleştir" : "pasifleştir";
    if (!(await confirmDelete(`${p.display_name || p.username} ${verb}ilsin mi?`))) return;
    try {
      await apiClient.post("/api/v1/waiter-profiles/onboard/", {
        username: p.username,
        is_active: next,
      });
      notifications.show({ message: next ? "Aktifleştirildi" : "Pasifleştirildi", color: "green" });
      qc.invalidateQueries({ queryKey: ["waiter-profiles"] });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  return (
    <Stack gap="md">
      <PageHeader
        title="Personel"
        subtitle={`${profiles.length} kayıtlı personel`}
        actions={<Button leftSection={<IconPlus size={16} />} onClick={startCreate}>Yeni Personel</Button>}
      />
      <Tabs defaultValue="profiles">
        <Tabs.List>
          <Tabs.Tab value="profiles">Profiller</Tabs.Tab>
          <Tabs.Tab value="salaries">Maaşlar</Tabs.Tab>
          <Tabs.Tab value="eotm">Ayın Elemanı</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="profiles" pt="md">
          {profiles.length === 0 ? <EmptyState title="Personel kaydı yok" /> : (
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
              {profiles.map((p: any) => (
                <Card key={p.id} padding="lg" radius="md" withBorder>
                  <Group justify="space-between" align="flex-start">
                    <Group>
                      <Avatar size="lg" radius="xl" color="blue" src={p.photo || undefined}>
                        {(p.display_name || p.username || "?")[0]?.toUpperCase()}
                      </Avatar>
                      <Stack gap={2}>
                        <Text fw={700}>{p.display_name || p.username}</Text>
                        <Text size="xs" c="dimmed">{p.nickname || p.phone || "-"}</Text>
                        <Group gap={4}>
                          {p.is_active ? <Badge size="xs" color="green">Aktif</Badge> : <Badge size="xs" color="gray">Pasif</Badge>}
                          {p.employee_id && <Badge size="xs" variant="light">{p.employee_id}</Badge>}
                        </Group>
                      </Stack>
                    </Group>
                    <Group gap={4}>
                      <ActionIcon
                        variant="subtle"
                        color={p.is_active ? "orange" : "green"}
                        title={p.is_active ? "Pasifleştir (girişi kapat)" : "Aktifleştir"}
                        onClick={() => handleToggleActive(p)}
                      >
                        {p.is_active ? <IconX size={16} /> : <IconCheck size={16} />}
                      </ActionIcon>
                      <ActionIcon variant="subtle" onClick={() => startEdit(p)}><IconEdit size={16} /></ActionIcon>
                      <ActionIcon variant="subtle" color="red" onClick={() => handleDelete(p)}><IconTrash size={16} /></ActionIcon>
                    </Group>
                  </Group>
                </Card>
              ))}
            </SimpleGrid>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="salaries" pt="md">
          <SalariesPanel />
        </Tabs.Panel>

        <Tabs.Panel value="eotm" pt="md">
          <EotmPanel pastSelections={eotm} />
        </Tabs.Panel>
      </Tabs>

      <PersonnelFormModal
        opened={formOpened}
        onClose={formCtl.close}
        editing={editing}
      />
    </Stack>
  );
}

/**
 * Personel formu — hem "Yeni Personel" hem "Personel Düzenle" için.
 *
 * `editing` null ise oluşturma, dolu ise düzenleme modu. Her iki modda da
 * AYNI alanlar gösterilir (kullanıcı isteği: düzenlemede eksik alan kalmasın).
 * Düzenlemede backend yine `onboard` endpoint'ine gider — endpoint mevcut
 * username'i bulunca User + Membership + WaiterProfile alanlarını günceller.
 *
 * Düzenleme modunda farklar:
 *  - Kullanıcı adı salt-okunur (kimlik anahtarı — değiştirilmez).
 *  - Şifre opsiyonel: boş bırakılırsa mevcut şifre korunur.
 */
function PersonnelFormModal({
  opened, onClose, editing,
}: {
  opened: boolean;
  onClose: () => void;
  editing: any | null;
}) {
  const isEdit = !!editing;
  const { data: branchData } = useResource<any>("branches");
  const branches: any[] = branchData?.results || branchData || [];
  const qc = useQueryClient();

  // Yeni seçilen fotoğraf dosyası (henüz yüklenmemiş). null = değişmedi.
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  // Düzenleme modunda bu personelin İÇİNDE BULUNULAN AYKİ maaş kaydını çek —
  // form "Aylık Sabit Maaş" alanını mevcut değerle doldurur, kullanıcı
  // güncelleyebilir. Yeni personelde sorgu çalışmaz (enabled: false).
  const now = new Date();
  const { data: currentSalaryData } = useResource<any>("salaries", {
    filters: {
      waiter_profile: editing?.id,
      period_year: now.getFullYear(),
      period_month: now.getMonth() + 1,
    },
    enabled: isEdit && !!editing?.id && opened,
  });
  const currentSalary: any | null =
    (currentSalaryData?.results || currentSalaryData || [])[0] || null;

  // Fotoğraf seçilirse multipart/form-data, seçilmezse düz JSON gönderilir.
  const save = useMutation({
    mutationFn: async (payload: Record<string, any>) => {
      if (!photoFile) {
        return (await apiClient.post("/api/v1/waiter-profiles/onboard/", payload)).data;
      }
      const fd = new FormData();
      Object.entries(payload).forEach(([k, v]) => {
        if (v === null || v === undefined) return;
        fd.append(k, typeof v === "boolean" ? String(v) : (v as any));
      });
      fd.append("photo", photoFile);
      return (await apiClient.post("/api/v1/waiter-profiles/onboard/", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["waiter-profiles"] });
      qc.invalidateQueries({ queryKey: ["salaries"] });
    },
  });

  const form = useForm({
    initialValues: {
      username: "",
      password: "",
      email: "",
      first_name: "",
      last_name: "",
      role: "WAITER",
      branch: "",
      display_name: "",
      nickname: "",
      phone: "",
      employee_id: "",
      hired_at: "",
      terminated_at: "",
      iban: "",
      notes: "",
      is_active: true,
      base_salary_lira: "" as number | "",
      commission_percent_override: "" as number | "",
    },
    validate: {
      username: (v) => (!v.trim() ? "Kullanıcı adı zorunlu" : null),
      // Şifre: oluşturmada zorunlu (min 6); düzenlemede boş geçilebilir,
      // dolu girilirse yine min 6 karakter.
      password: (v) => {
        if (!isEdit) return v.length < 6 ? "Şifre en az 6 karakter" : null;
        return v && v.length < 6 ? "Şifre en az 6 karakter" : null;
      },
      email: validateEmail,
      hired_at: validateDate,
      terminated_at: validateDate,
      phone: validatePhoneTR,
    },
  });

  // Modal açılınca formu doldur: düzenlemede mevcut kaydın değerleri,
  // oluşturmada boş varsayılanlar. `editing.user` ilişkili auth.User —
  // username/ad/soyad/e-posta oradan; rol Membership'ten gelmeyebilir,
  // serializer döndürmediği alan boş kalır (kullanıcı yine seçer).
  useEffect(() => {
    if (!opened) return;
    setPhotoFile(null);  // her açılışta seçili dosyayı temizle
    if (editing) {
      form.setValues({
        username: editing.username || "",
        password: "",
        email: editing.email || "",
        first_name: editing.first_name || "",
        last_name: editing.last_name || "",
        role: editing.role || "WAITER",
        branch: editing.branch || "",
        display_name: editing.display_name || "",
        nickname: editing.nickname || "",
        phone: editing.phone || "",
        employee_id: editing.employee_id || "",
        hired_at: editing.hired_at || "",
        terminated_at: editing.terminated_at || "",
        iban: editing.iban || "",
        notes: editing.notes || "",
        is_active: editing.is_active ?? true,
        base_salary_lira: "",
        commission_percent_override:
          editing.commission_percent_override == null
            ? ""
            : Number(editing.commission_percent_override),
      });
    } else {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, editing]);

  // Bu ayki maaş kaydı geldiğinde "Aylık Sabit Maaş" alanını doldur.
  // Ayrı effect: salary sorgusu form.setValues'tan sonra async tamamlanır.
  useEffect(() => {
    if (!opened || !isEdit) return;
    const minor = currentSalary?.base_salary_minor;
    form.setFieldValue(
      "base_salary_lira",
      minor != null && minor > 0 ? Number(minor) / 100 : "",
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, isEdit, currentSalary?.id, currentSalary?.base_salary_minor]);

  async function handleSubmit(values: typeof form.values) {
    const { base_salary_lira, ...rest } = values;
    const payload: Record<string, any> = {
      ...rest,
      branch: values.branch || null,
      hired_at: values.hired_at.trim() || null,
      terminated_at: values.terminated_at.trim() || null,
      // Telefon standart 05XXXXXXXXX biçiminde kaydedilir.
      phone: values.phone ? normalizePhone(values.phone) : "",
      commission_percent_override:
        values.commission_percent_override === "" ? null : values.commission_percent_override,
    };
    // Düzenlemede boş şifre gönderme — backend boş şifreyi "değiştirme" sayar.
    if (isEdit && !values.password) delete payload.password;
    // Sabit maaş:
    //  - Düzenlemede alan boş değilse her zaman gönder (0 dahil) — kullanıcı
    //    bu ayki bordroyu güncelleyebilsin. Boşsa hiç gönderme (kayda dokunma).
    //  - Yeni personelde sadece > 0 ise gönder (sıfır maaş kaydı oluşturmasın).
    if (base_salary_lira !== "") {
      const minor = Math.round(Number(base_salary_lira) * 100);
      if (isEdit || minor > 0) payload.base_salary_minor = minor;
    }
    try {
      await save.mutateAsync(payload);
      notifications.show({
        message: isEdit ? "Personel güncellendi" : "Personel oluşturuldu",
        color: "green",
      });
      form.reset();
      onClose();
    } catch (e: any) {
      notifications.show({
        message: e?.response?.data?.detail || JSON.stringify(e?.response?.data || e),
        color: "red",
      });
    }
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? "Personel Düzenle" : "Yeni Personel"}
      size="md"
    >
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          {/* Personel fotoğrafı — yeni seçilen dosya yoksa mevcut foto gösterilir */}
          <Group>
            {photoFile ? (
              <Image
                src={URL.createObjectURL(photoFile)}
                w={72}
                h={72}
                radius="md"
                fit="cover"
                alt="Yeni fotoğraf"
              />
            ) : editing?.photo ? (
              <Image
                src={editing.photo}
                w={72}
                h={72}
                radius="md"
                fit="cover"
                alt="Personel fotoğrafı"
              />
            ) : (
              <Avatar size={72} radius="md" color="blue">
                <IconPhoto size={28} />
              </Avatar>
            )}
            <Stack gap={4}>
              <FileButton
                onChange={setPhotoFile}
                accept="image/png,image/jpeg,image/webp"
              >
                {(props) => (
                  <Button {...props} variant="light" size="xs" leftSection={<IconPhoto size={14} />}>
                    {photoFile || editing?.photo ? "Fotoğrafı Değiştir" : "Fotoğraf Seç"}
                  </Button>
                )}
              </FileButton>
              {photoFile && (
                <Button
                  variant="subtle"
                  color="red"
                  size="compact-xs"
                  leftSection={<IconX size={12} />}
                  onClick={() => setPhotoFile(null)}
                >
                  Seçimi iptal et
                </Button>
              )}
              <Text size="xs" c="dimmed">PNG / JPEG / WebP</Text>
            </Stack>
          </Group>

          <TextInput
            label="Kullanıcı adı"
            required
            readOnly={isEdit}
            description={isEdit ? "Kullanıcı adı değiştirilemez" : undefined}
            {...form.getInputProps("username")}
          />
          <PasswordInput
            label={isEdit ? "Yeni Şifre" : "Şifre"}
            required={!isEdit}
            placeholder={isEdit ? "Boş bırakırsanız değişmez" : undefined}
            description={isEdit ? "Yalnızca şifre değiştirmek istiyorsanız doldurun" : undefined}
            {...form.getInputProps("password")}
          />
          <Group grow>
            <TextInput label="Ad" {...form.getInputProps("first_name")} />
            <TextInput label="Soyad" {...form.getInputProps("last_name")} />
          </Group>
          <TextInput label="E-posta" type="email" {...form.getInputProps("email")} />
          <Select
            label="Rol"
            required
            data={ROLE_OPTIONS}
            {...form.getInputProps("role")}
          />
          {branches.length > 0 && (
            <Select
              label="Şube (opsiyonel — boş ise tüm şubeler)"
              data={branches.map((b: any) => ({ value: b.id, label: b.name }))}
              clearable
              {...form.getInputProps("branch")}
            />
          )}
          <Group grow>
            <TextInput label="Görünen İsim" {...form.getInputProps("display_name")} />
            <TextInput label="Lakap" {...form.getInputProps("nickname")} />
          </Group>
          <Group grow>
            <TextInput label="Telefon" {...form.getInputProps("phone")} />
            <TextInput label="Personel No" {...form.getInputProps("employee_id")} />
          </Group>
          <Group grow>
            <DatePickerInput
              label="İşe Giriş"
              placeholder="Tarih seçin"
              valueFormat="DD.MM.YYYY"
              clearable
              value={toDateValue(form.values.hired_at)}
              onChange={(v) => form.setFieldValue("hired_at", toDateStr(v))}
              error={form.errors.hired_at}
            />
            <DatePickerInput
              label="İşten Ayrılış"
              placeholder="Çalışıyorsa boş bırak"
              valueFormat="DD.MM.YYYY"
              clearable
              value={toDateValue(form.values.terminated_at)}
              onChange={(v) => form.setFieldValue("terminated_at", toDateStr(v))}
              error={form.errors.terminated_at}
            />
          </Group>
          <TextInput label="IBAN" {...form.getInputProps("iban")} />
          <Textarea label="Notlar" autosize minRows={2} {...form.getInputProps("notes")} />

          <Divider label="Maaş & Sözleşme" labelPosition="left" mt="xs" />
          <Group grow>
            <NumberInput
              label="Aylık Sabit Maaş (₺)"
              description={
                isEdit
                  ? "Bu ayki maaş kaydının sabit maaşı — değiştirip kaydedebilirsiniz"
                  : "Bu ay için maaş kaydı oluşturulur"
              }
              placeholder="0,00"
              min={0}
              decimalScale={2}
              fixedDecimalScale
              thousandSeparator="."
              decimalSeparator=","
              suffix=" ₺"
              {...(form.getInputProps("base_salary_lira") as any)}
            />
            <NumberInput
              label="Prim Yüzdesi (%)"
              description="Boş ise tenant varsayılanı kullanılır"
              placeholder="örn. 2.5"
              min={0}
              max={100}
              decimalScale={2}
              {...(form.getInputProps("commission_percent_override") as any)}
            />
          </Group>
          <Switch label="Aktif" {...form.getInputProps("is_active", { type: "checkbox" })} />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>İptal</Button>
            <Button type="submit" loading={save.isPending}>
              {isEdit ? "Kaydet" : "Oluştur"}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}

function SalariesPanel() {
  const qc = useQueryClient();
  const {
    items: salaries,
    total,
    isLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteResource<any>("salaries");

  const [payModal, setPayModal] = useState<any | null>(null);

  const generate = useMutation({
    mutationFn: async () =>
      (await apiClient.post("/api/v1/salaries/generate-period/", {})).data,
    onSuccess: (data: any) => {
      qc.invalidateQueries({ queryKey: ["salaries"] });
      notifications.show({
        message: data?.detail || "Maaş kayıtları oluşturuldu",
        color: data?.created > 0 ? "green" : "blue",
      });
    },
    onError: (e: any) => {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    },
  });

  const unmark = useMutation({
    mutationFn: async (id: string) =>
      (await apiClient.post(`/api/v1/salaries/${id}/unmark-paid/`, {})).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salaries"] });
      notifications.show({ message: "Ödendi işareti geri alındı", color: "blue" });
    },
    onError: (e: any) => {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    },
  });

  async function handleUnmark(s: any) {
    if (!(await confirmAction({
      title: "Ödendi işaretini geri al",
      message: `${s.waiter_name || "Personel"} — ${s.period_year}/${String(s.period_month).padStart(2, "0")} dönemi tekrar "Bekliyor" durumuna alınacak.`,
      confirmLabel: "Geri Al",
    }))) return;
    unmark.mutate(s.id);
  }

  const pendingCount = salaries.filter((s: any) => !s.paid_at).length;

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {total} maaş kaydı
          {pendingCount > 0 && ` — ${pendingCount} ödeme bekliyor`}
        </Text>
        <Button
          variant="light"
          leftSection={<IconCalendarPlus size={16} />}
          loading={generate.isPending}
          onClick={() => generate.mutate()}
        >
          Bu Ayın Maaşlarını Oluştur
        </Button>
      </Group>

      <Card padding="sm" radius="md" withBorder>
        <InfiniteTable
          rows={salaries}
          rowKey={(s) => s.id}
          isLoading={isLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          fetchNextPage={fetchNextPage}
          emptyContent={<EmptyState title="Maaş kaydı yok" />}
          columns={[
            {
              header: "Dönem",
              render: (s) => `${s.period_year}/${String(s.period_month).padStart(2, "0")}`,
            },
            { header: "Personel", render: (s) => s.waiter_name || "-" },
            {
              header: "Çalışılan Gün",
              align: "center",
              render: (s) => {
                const isPartial = (s.worked_days ?? s.period_days) < (s.period_days ?? 30);
                const ratioPct = Math.round((s.proration_ratio ?? 1) * 100);
                return isPartial ? (
                  <Tooltip label={`Ayın ortasında işe giriş/çıkış — maaş %${ratioPct} oranında`}>
                    <Badge color="orange" variant="light">
                      {s.worked_days}/{s.period_days} gün (%{ratioPct})
                    </Badge>
                  </Tooltip>
                ) : (
                  <Text size="sm" c="dimmed">{s.worked_days}/{s.period_days}</Text>
                );
              },
            },
            {
              header: "Sabit Maaş",
              align: "right",
              render: (s) => {
                const isPartial = (s.worked_days ?? s.period_days) < (s.period_days ?? 30);
                return (
                  <>
                    <MoneyDisplay minor={s.base_salary_minor || 0} />
                    {isPartial && <Text size="xs" c="dimmed">tam ay</Text>}
                  </>
                );
              },
            },
            {
              header: "Prim",
              align: "right",
              render: (s) => <MoneyDisplay minor={s.commission_minor || 0} />,
            },
            {
              header: "Bahşiş",
              align: "right",
              render: (s) => <MoneyDisplay minor={s.tips_minor || 0} />,
            },
            {
              header: "Avans",
              align: "right",
              render: (s) => <MoneyDisplay minor={s.advances_minor || 0} />,
            },
            {
              header: "Net",
              align: "right",
              render: (s) => <MoneyDisplay minor={s.net_payable_minor || 0} fw={600} />,
            },
            {
              header: "Ödeme",
              render: (s) =>
                s.paid_at ? (
                  <Stack gap={2}>
                    <Badge color="green">Ödendi</Badge>
                    {s.payment_reference && (
                      <Group gap={4} wrap="nowrap">
                        <IconReceipt size={12} />
                        <Text size="xs" c="dimmed">{s.payment_reference}</Text>
                      </Group>
                    )}
                    <Text size="xs" c="dimmed">
                      {new Date(s.paid_at).toLocaleDateString("tr-TR")}
                    </Text>
                  </Stack>
                ) : (
                  <Badge color="orange">Bekliyor</Badge>
                ),
            },
            {
              header: "",
              render: (s) =>
                s.paid_at ? (
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color="gray"
                    leftSection={<IconX size={12} />}
                    onClick={() => handleUnmark(s)}
                  >
                    Geri Al
                  </Button>
                ) : (
                  <Button
                    size="compact-xs"
                    variant="light"
                    color="green"
                    leftSection={<IconCheck size={12} />}
                    onClick={() => setPayModal(s)}
                  >
                    Ödendi İşaretle
                  </Button>
                ),
            },
          ]}
        />
      </Card>

      <MarkPaidModal record={payModal} onClose={() => setPayModal(null)} />
    </Stack>
  );
}

function MarkPaidModal({ record, onClose }: { record: any | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [reference, setReference] = useState("");
  const [paidAt, setPaidAt] = useState("");

  useEffect(() => {
    if (record) {
      setReference("");
      setPaidAt(new Date().toISOString().slice(0, 10));
    }
  }, [record]);

  const markPaid = useMutation({
    mutationFn: async (payload: Record<string, any>) =>
      (await apiClient.post(`/api/v1/salaries/${record.id}/mark-paid/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["salaries"] });
      notifications.show({ message: "Maaş ödendi olarak işaretlendi", color: "green" });
      onClose();
    },
    onError: (e: any) => {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    },
  });

  function handleSubmit() {
    const payload: Record<string, any> = {
      payment_reference: reference.trim(),
    };
    if (paidAt.trim()) payload.paid_at = paidAt.trim();
    markPaid.mutate(payload);
  }

  return (
    <Modal opened={!!record} onClose={onClose} title="Maaş Ödemesi" size="md">
      {record && (
        <Stack gap="md">
          <Card padding="sm" radius="md" withBorder bg="green.0">
            <Group justify="space-between">
              <Stack gap={2}>
                <Text fw={700}>{record.waiter_name || "Personel"}</Text>
                <Text size="sm" c="dimmed">
                  {record.period_year}/{String(record.period_month).padStart(2, "0")} dönemi
                </Text>
              </Stack>
              <MoneyDisplay minor={record.net_payable_minor || 0} fw={700} />
            </Group>
          </Card>
          <TextInput
            label="Ödeme Tarihi"
            type="date"
            value={paidAt}
            onChange={(e) => setPaidAt(e.currentTarget.value)}
          />
          <TextInput
            label="Ödeme Dekontu / Referans No"
            placeholder="örn. Banka havale no, dekont no..."
            value={reference}
            onChange={(e) => setReference(e.currentTarget.value)}
            data-autofocus
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={onClose}>İptal</Button>
            <Button
              color="green"
              leftSection={<IconCheck size={16} />}
              loading={markPaid.isPending}
              onClick={handleSubmit}
            >
              Ödendi Olarak İşaretle
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}

function EotmPanel({ pastSelections }: { pastSelections: any[] }) {
  const now = new Date();
  const [year, setYear] = useState<number>(now.getFullYear());
  const [month, setMonth] = useState<number>(now.getMonth() + 1);
  const [selectModal, setSelectModal] = useState<any | null>(null);

  const qc = useQueryClient();
  const { data: lb, isLoading } = useQuery<any>({
    queryKey: ["waiter-leaderboard", year, month],
    queryFn: async () =>
      (await apiClient.get("/api/v1/waiter-profiles/leaderboard/", {
        params: { year, month },
      })).data,
  });

  const select = useMutation({
    mutationFn: async (payload: Record<string, any>) =>
      (await apiClient.post("/api/v1/employee-of-month/select/", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["employee-of-month"] });
      qc.invalidateQueries({ queryKey: ["waiter-leaderboard"] });
      notifications.show({ message: "Ayın Elemanı seçildi", color: "green" });
      setSelectModal(null);
    },
    onError: (e: any) => {
      notifications.show({
        message: e?.response?.data?.detail || "Hata",
        color: "red",
      });
    },
  });

  const rows: any[] = lb?.rows || [];
  const alreadySelected = pastSelections.find(
    (e: any) => e.period_year === year && e.period_month === month,
  );

  const yearOptions = Array.from({ length: 5 }, (_, i) => ({
    value: String(now.getFullYear() - i),
    label: String(now.getFullYear() - i),
  }));
  const monthOptions = [
    "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
    "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
  ].map((label, i) => ({ value: String(i + 1), label }));

  return (
    <Stack gap="md">
      <Card padding="md" radius="md" withBorder>
        <Group justify="space-between" mb="sm">
          <Text fw={700}>📊 Satış Sıralaması</Text>
          <Group gap="xs">
            <Select
              size="xs"
              w={90}
              data={monthOptions}
              value={String(month)}
              onChange={(v) => v && setMonth(Number(v))}
            />
            <Select
              size="xs"
              w={90}
              data={yearOptions}
              value={String(year)}
              onChange={(v) => v && setYear(Number(v))}
            />
          </Group>
        </Group>
        {alreadySelected && (
          <Badge color="green" mb="sm">
            ✓ Bu ay için {alreadySelected.waiter_username} seçilmiş
          </Badge>
        )}
        {isLoading ? <Loader size="sm" /> : rows.length === 0 ? (
          <EmptyState title="Bu ay için sipariş kaydı yok" />
        ) : (
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>#</Table.Th>
                <Table.Th>Personel</Table.Th>
                <Table.Th ta="right">Sipariş</Table.Th>
                <Table.Th ta="right">Kapalı</Table.Th>
                <Table.Th ta="right">Ciro</Table.Th>
                <Table.Th ta="right">Bahşiş</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r: any, i: number) => (
                <Table.Tr key={r.waiter_id}>
                  <Table.Td>
                    {i === 0 ? "🏆" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                  </Table.Td>
                  <Table.Td fw={i === 0 ? 700 : 500}>
                    {r.display_name || r.username}
                  </Table.Td>
                  <Table.Td ta="right">{r.order_count}</Table.Td>
                  <Table.Td ta="right">{r.closed_count}</Table.Td>
                  <Table.Td ta="right">
                    <MoneyDisplay minor={r.revenue_minor} fw={600} />
                  </Table.Td>
                  <Table.Td ta="right">
                    <MoneyDisplay minor={r.tips_minor} />
                  </Table.Td>
                  <Table.Td>
                    {r.waiter_profile_id && !alreadySelected && (
                      <Button
                        size="xs"
                        variant={i === 0 ? "filled" : "light"}
                        onClick={() => setSelectModal({ row: r, year, month })}
                      >
                        Seç
                      </Button>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Card>

      <Divider label="Geçmiş Seçimler" labelPosition="left" />
      {pastSelections.length === 0 ? (
        <EmptyState title="Henüz ayın elemanı seçilmemiş" />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          {pastSelections.map((e: any) => (
            <Card key={e.id} padding="lg" radius="md" withBorder>
              <Stack gap="xs">
                <Text fw={700} size="lg">🏆 {e.waiter_username}</Text>
                <Text size="sm" c="dimmed">
                  {e.period_year}/{String(e.period_month).padStart(2, "0")}
                </Text>
                {e.reason && <Text size="sm">{e.reason}</Text>}
                <Group gap="xs">
                  <Badge color="yellow">{e.snapshot_order_count} sipariş</Badge>
                  <MoneyDisplay minor={e.snapshot_revenue_minor || 0} fw={600} />
                </Group>
              </Stack>
            </Card>
          ))}
        </SimpleGrid>
      )}

      <EotmSelectModal
        ctx={selectModal}
        onClose={() => setSelectModal(null)}
        onSubmit={(reason) =>
          select.mutate({
            waiter_profile: selectModal.row.waiter_profile_id,
            year: selectModal.year,
            month: selectModal.month,
            reason,
          })
        }
        pending={select.isPending}
      />
    </Stack>
  );
}

function EotmSelectModal({
  ctx, onClose, onSubmit, pending,
}: {
  ctx: any | null;
  onClose: () => void;
  onSubmit: (reason: string) => void;
  pending: boolean;
}) {
  const [reason, setReason] = useState("");
  return (
    <Modal opened={!!ctx} onClose={onClose} title="Ayın Elemanı Seç" size="md">
      {ctx && (
        <Stack gap="md">
          <Card padding="sm" radius="md" withBorder bg="yellow.0">
            <Group>
              <Text size="2rem">🏆</Text>
              <Stack gap={2}>
                <Text fw={700}>{ctx.row.display_name || ctx.row.username}</Text>
                <Text size="sm" c="dimmed">
                  {ctx.year}/{String(ctx.month).padStart(2, "0")} —{" "}
                  {ctx.row.order_count} sipariş
                </Text>
                <MoneyDisplay minor={ctx.row.revenue_minor} fw={600} />
              </Stack>
            </Group>
          </Card>
          <TextInput
            label="Seçim Sebebi"
            placeholder="örn. En yüksek ciro, müşteri memnuniyeti..."
            value={reason}
            onChange={(e) => setReason(e.currentTarget.value)}
            data-autofocus
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>İptal</Button>
            <Button onClick={() => onSubmit(reason)} loading={pending}>
              Seç
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
